import { useTranslation } from 'next-i18next';
import React, { useMemo, VFC } from 'react';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { useTokenVersions } from '@/apis/theGraph/aniftyToken/getTokenVersions';
import CollapsableBox from '@/components/CollapsableBox';
import { addresses } from '@/constants/addresses';
import createMiddleEllipsis from '@/utils/createMiddleEllipsis';
import { getEtherscanAddressLink } from '@/utils/etherscan';

import styles from './AutenticityProofBox.module.scss';

type Props = {
  creation: V2CreationData;
  tokenID?: number;
  blockchain?: 'Ethereum';
};

const AuthenticityProofBox: VFC<Props> = ({
  creation,
  tokenID,
  blockchain = 'Ethereum',
}: Props) => {
  const { t } = useTranslation();

  const { data: tokenVersions } = useTokenVersions({
    tokenID,
  });

  const tokenContractAddress = useMemo(() => {
    if (
      tokenVersions &&
      tokenVersions.length === 1 &&
      tokenVersions.includes(1)
    ) {
      // token is minted only on v1 contract
      return addresses.AniftyERC1155V1;
    }

    if (
      creation?.mintAsk &&
      (creation.mintAsk.tokenVersionID == null ||
        creation.mintAsk.tokenVersionID === 1)
    ) {
      // token is not minted and has v1 token version
      return addresses.AniftyERC1155V1;
    }

    // otherwise, token is or should be minted on v2 contract
    return addresses.AniftyERC1155V2;
  }, [creation, addresses, tokenVersions]);

  return (
    <CollapsableBox heading="Proof of Authenticity">
      <dl className={styles.authenticityList}>
        {/* Contract address */}
        <dt>{t('creationPage.contractAddress')}</dt>
        <dd>
          <a
            href={getEtherscanAddressLink(tokenContractAddress)}
            target="_blank"
            rel="noreferrer"
          >
            {createMiddleEllipsis(tokenContractAddress)}
          </a>
        </dd>

        {/* Token ID */}
        {typeof tokenID === 'number' && tokenID > 0 ? (
          <>
            <dt>{t('creationPage.tokenID')}</dt>
            <dd>{tokenID}</dd>
          </>
        ) : null}

        {/* Blockchain */}
        <dt>{t('creationPage.blockchain')}</dt>
        <dd>
          {blockchain === 'Ethereum' ? t('creationPage.ethereum') : blockchain}
        </dd>
      </dl>
    </CollapsableBox>
  );
};

export default AuthenticityProofBox;
